import axios, { AxiosInstance } from 'axios';

/**
 * Admin API 클라이언트
 * Admin API client
 */
const getAdminApiInstance = (): AxiosInstance => {
  // 환경 변수 사용 (빌드 타임에 설정됨)
  // 로컬 개발: VITE_API_BASE_URL=http://localhost:9000
  // 운영 환경: VITE_API_BASE_URL=https://ailic.kpc.or.kr
  // 백엔드 컨트롤러에 이미 /api가 포함되어 있으므로 baseURL에는 /api 제외
  const baseURL = import.meta.env.VITE_API_BASE_URL;
  
  const instance = axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // 요청 인터셉터: JWT 토큰 추가
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('adminAuthToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // 응답 인터셉터: 401 에러 처리
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // 401 에러만 처리 (403은 권한 문제이므로 로그아웃하지 않음)
      if (error.response?.status === 401) {
        const currentPath = window.location.pathname;
        const requestUrl = error.config?.url || '';
        const isLoginPage = currentPath.includes('/admin/login');
        const isLoginApi = requestUrl.includes('/api/admin/auth/login');
        
        // 로그인 API나 로그인 페이지가 아닐 때만 처리
        if (!isLoginPage && !isLoginApi) {
          // 인증 상태 초기화 이벤트 발생 (useAdminAuth가 감지하도록)
          // localStorage 제거는 useAdminAuth에서 처리하도록 함
          window.dispatchEvent(new CustomEvent('admin-auth-401', { 
            detail: { 
              path: currentPath,
              error: error.response?.data,
              config: error.config
            } 
          }));
        }
      }
      // 403 에러는 처리하지 않음 (권한 문제는 로그인 문제가 아님)
      return Promise.reject(error);
    }
  );

  return instance;
};

const adminApiClient = getAdminApiInstance();

/**
 * Admin API functions
 * 관리자 API 함수들
 */
export const adminApi = {
  // Auth / 인증
  login: async (username: string, password: string) => {
    const response = await adminApiClient.post('/api/admin/auth/login', {
      username,
      password,
    });
    return response.data;
  },

  getMe: async () => {
    const response = await adminApiClient.get('/api/admin/auth/me');
    return response.data;
  },

  // Admin Users / 관리자 사용자
  getAdminUsers: async () => {
    const response = await adminApiClient.get('/api/admin/users');
    return response.data;
  },

  getAdminUserById: async (id: string) => {
    const response = await adminApiClient.get(`/api/admin/users/${id}`);
    return response.data;
  },

  createAdminUser: async (data: any) => {
    const response = await adminApiClient.post('/api/admin/users', data);
    return response.data;
  },

  updateAdminUser: async (id: string, data: any) => {
    const response = await adminApiClient.put(`/api/admin/users/${id}`, data);
    return response.data;
  },

  deleteAdminUser: async (id: string) => {
    const response = await adminApiClient.delete(`/api/admin/users/${id}`);
    return response.data;
  },

  // System Users / 시스템 사용자
  getSystemUsers: async () => {
    const response = await adminApiClient.get('/api/admin/system-users');
    return response.data;
  },

  getSystemUserById: async (id: string) => {
    const response = await adminApiClient.get(`/api/admin/system-users/${id}`);
    return response.data;
  },

  updateSystemUser: async (id: string, data: any) => {
    const response = await adminApiClient.put(`/api/admin/system-users/${id}`, data);
    return response.data;
  },

  deleteSystemUser: async (id: string) => {
    const response = await adminApiClient.delete(`/api/admin/system-users/${id}`);
    return response.data;
  },

  // Configs / 설정
  getConfigs: async () => {
    const response = await adminApiClient.get('/api/admin/configs');
    return response.data;
  },

  getConfigByKey: async (key: string) => {
    const response = await adminApiClient.get(`/api/admin/configs/${key}`);
    return response.data;
  },

  createConfig: async (data: any) => {
    const response = await adminApiClient.post('/api/admin/configs', data);
    return response.data;
  },

  updateConfig: async (key: string, data: any) => {
    const response = await adminApiClient.put(`/api/admin/configs/${key}`, data);
    return response.data;
  },

  deleteConfig: async (key: string) => {
    const response = await adminApiClient.delete(`/api/admin/configs/${key}`);
    return response.data;
  },

  // Logs / 로그
  searchLogs: async (params: { userId?: string; experimentKey?: string; page?: number; limit?: number }) => {
    const response = await adminApiClient.get('/api/admin/logs', { params });
    return response.data;
  },

  getExportLogsCount: async (params: { userId?: string; experimentKey?: string }) => {
    const response = await adminApiClient.get('/api/admin/logs/export/count', { params });
    return response.data;
  },

  exportLogs: async (params: { userId?: string; experimentKey?: string }) => {
    const response = await adminApiClient.get('/api/admin/logs/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  deleteLogsByExperimentKey: async (experimentKey: string) => {
    const response = await adminApiClient.delete(`/api/admin/logs/${experimentKey}`);
    return response.data;
  },

  // Image Logs / 이미지 로그
  searchImageLogs: async (params: { userId?: string; experimentKey?: string; page?: number; limit?: number }) => {
    const response = await adminApiClient.get('/api/admin/image-logs', { params });
    return response.data;
  },

  getExportImageLogsCount: async (params: { userId?: string; experimentKey?: string }) => {
    const response = await adminApiClient.get('/api/admin/image-logs/export/count', { params });
    return response.data;
  },

  exportImageLogs: async (params: { userId?: string; experimentKey?: string }) => {
    const response = await adminApiClient.get('/api/admin/image-logs/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  deleteImageLogsByExperimentKey: async (experimentKey: string) => {
    const response = await adminApiClient.delete(`/api/admin/image-logs/${experimentKey}`);
    return response.data;
  },

  // Dashboard Statistics / 대시보드 통계
  getDashboardStats: async () => {
    const response = await adminApiClient.get('/api/admin/dashboard/stats');
    return response.data;
  },

  getLogStatsByDateRange: async (days?: number) => {
    const response = await adminApiClient.get('/api/admin/dashboard/logs-by-date', {
      params: { days },
    });
    return response.data;
  },

  getTopUsers: async (limit?: number) => {
    const response = await adminApiClient.get('/api/admin/dashboard/top-users', {
      params: { limit },
    });
    return response.data;
  },

  getTopExperimentKeys: async (limit?: number) => {
    const response = await adminApiClient.get('/api/admin/dashboard/top-experiment-keys', {
      params: { limit },
    });
    return response.data;
  },

  getTokenUsageStats: async () => {
    const response = await adminApiClient.get('/api/admin/dashboard/token-usage');
    return response.data;
  },

  getRecentActivity: async (limit?: number) => {
    const response = await adminApiClient.get('/api/admin/dashboard/recent-activity', {
      params: { limit },
    });
    return response.data;
  },
};

export default adminApi;
